<template>
  <div class="c-drop-uploader">
    <div
      class="c-drop-uploader__zone"
      :class="{ 'c-drop-uploader__zone--active': dragging }"
      @click="choose"
      @dragover.prevent="onDragOver"
      @dragenter.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <input
        ref="input"
        style="display: none"
        type="file"
        :accept="accept"
        multiple
        @change="onSelect"
      />
      <img :src="require('base/upload.svg')" class="c-drop-uploader__icon" />
      <div class="c-drop-uploader__text">
        <strong>Drag files here</strong> or click to browse
      </div>
    </div>

    <div v-if="uploads.length" class="c-drop-uploader__list">
      <div
        v-for="(upload, index) in uploads"
        :key="index"
        class="c-drop-uploader__list-item"
        :class="`c-drop-uploader__list-item--${upload.status}`"
      >
        <span class="c-drop-uploader__list-name">{{ upload.name }}</span>
        <span class="c-drop-uploader__list-status">
          <template v-if="upload.status === 'uploading'">Uploading…</template>
          <template v-else-if="upload.status === 'done'">Uploaded</template>
          <template v-else>{{ upload.message }}</template>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { objectToFormData } from "object-to-formdata";

export default {
  name: "c-drop-uploader",
  props: {
    // Endpoint to POST each file to.
    path: {
      type: String,
      required: true
    },
    // Top-level param key, e.g. "document" or "photo".
    submitModel: {
      type: String,
      required: true
    },
    // File field name inside submitModel, e.g. "file".
    name: {
      type: String,
      default: "file"
    },
    // Extra params merged alongside the file, e.g. { parent_type, parent_id }.
    params: {
      type: Object,
      default: () => ({})
    },
    // Native accept attribute for the browse dialog.
    accept: {
      type: String,
      default: ""
    },
    // Allowed MIME types for the client-side pre-check (empty = rely on server).
    acceptTypes: {
      type: Array,
      default: () => []
    },
    maxSizeMb: {
      type: Number,
      default: 100
    }
  },
  data() {
    return {
      dragging: false,
      uploads: []
    };
  },
  methods: {
    choose() {
      this.$refs.input.click();
    },
    onDragOver() {
      this.dragging = true;
    },
    onDragLeave() {
      this.dragging = false;
    },
    onDrop(event) {
      this.dragging = false;
      this.handleFiles(event.dataTransfer.files);
    },
    onSelect() {
      this.handleFiles(this.$refs.input.files);
      this.$refs.input.value = null;
    },
    handleFiles(fileList) {
      const files = Array.from(fileList || []);
      files.forEach(file => this.uploadFile(file));
    },
    validationError(file) {
      if (file.size > this.maxSizeMb * 1024 * 1024) {
        return `size must be less than ${this.maxSizeMb}mb.`;
      }
      if (
        this.acceptTypes.length &&
        file.type &&
        !this.acceptTypes.includes(file.type)
      ) {
        return "type not allowed.";
      }
      return null;
    },
    uploadFile(file) {
      const entry = this.trackUpload(file.name, "uploading");

      const error = this.validationError(file);
      if (error) {
        entry.status = "error";
        entry.message = error;
        this.$emit("error", { file, message: error });
        return;
      }

      const formData = objectToFormData({
        [this.submitModel]: {
          ...this.params,
          [this.name]: file
        }
      });

      this.$http.post(this.path, formData).then(
        response => {
          entry.status = "done";
          this.$emit("uploaded", response.body);
        },
        response => {
          const message =
            (response.body && response.body.errors
              ? response.body.errors.join("\n")
              : null) || "Upload failed.";
          entry.status = "error";
          entry.message = message;
          this.$emit("error", { file, message });
        }
      );
    },
    // Push a tracked upload row and return the reactive object so callers mutate it in place.
    trackUpload(name, status) {
      const entry = { name, status, message: "" };
      this.uploads.push(entry);
      return this.uploads[this.uploads.length - 1];
    }
  }
};
</script>

<style scoped lang="scss">
.c-drop-uploader {
  &__zone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 20px;
    border: 2px dashed var(--gray2);
    border-radius: 6px;
    background-color: var(--gray1);
    color: var(--gray4);
    cursor: pointer;
    text-align: center;
    transition: border-color 0.15s ease, background-color 0.15s ease;

    &:hover {
      border-color: var(--blue);
    }

    &--active {
      border-color: var(--blue);
      background-color: rgba(0, 123, 255, 0.06);
      color: var(--dark);
    }
  }

  &__icon {
    width: 24px;
    height: 24px;
    opacity: 0.7;
  }

  &__text {
    font-size: 0.9rem;
  }

  &__list {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__list-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 0.85rem;
    background-color: var(--gray1);
    border: 1px solid var(--gray2);

    &--done {
      border-color: var(--green, #28a745);
    }

    &--error {
      border-color: var(--red, #dc3545);
      color: var(--red, #dc3545);
    }
  }

  &__list-name {
    word-break: break-all;
    margin-right: 10px;
  }

  &__list-status {
    white-space: nowrap;
    font-weight: 500;
  }
}
</style>
